import React, { useEffect, useRef } from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";

export const ReactbitsGlowBackground: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const numParticles = 24;
  const gridSize = 90; // Size of squares in grid

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Helper to determine square glow deterministically
    const getCellGlow = (col: number, row: number, currentFrame: number) => {
      // Generate cell seed
      const seed = Math.sin(col * 31.17 + row * 89.93) * 43758.5453;
      const rand = seed - Math.floor(seed);
      const speed = 0.015 + (col % 4) * 0.008;
      const val = Math.sin(currentFrame * speed + rand * Math.PI * 2);

      if (val > 0.6) {
        const intensity = (val - 0.6) / 0.4;
        const colorType = (col + row * 3) % 3; // 0: Cyan, 1: Pink, 2: Lime
        return { active: true, intensity, colorType };
      }
      return { active: false, intensity: 0, colorType: 0 };
    };

    // Helper to get floating particles deterministically
    const getParticle = (i: number, currentFrame: number) => {
      const seedX = Math.sin(i * 182.23) * 1000;
      const seedY = Math.cos(i * 243.81) * 1000;
      const randSpeed = Math.sin(i * 92.47);

      const baseX = (Math.abs(seedX) % (width - 160)) + 80;
      const baseY = (Math.abs(seedY) % (height - 240)) + 120;

      const speedX = 0.008 + Math.abs(randSpeed % 0.01);
      const speedY = 0.006 + Math.abs((randSpeed * 1.5) % 0.01);

      const rangeX = 80 + (i % 4) * 45;
      const rangeY = 120 + (i % 4) * 55;

      const offset = i * 3.5;

      const x = baseX + Math.sin(currentFrame * speedX + offset) * rangeX;
      const y = baseY + Math.cos(currentFrame * speedY + offset) * rangeY;

      const size = 6 + (i % 4);
      const colorType = i % 3; // 0: Cyan, 1: Pink, 2: Lime

      return { x, y, size, colorType };
    };

    // 1. Dark tech background fill
    ctx.fillStyle = "#060713"; // Rich ultra-dark navy
    ctx.fillRect(0, 0, width, height);

    // 2. Ambient Colorful Aurora Glows (Slowly moving)
    const angle = (frame / 200) * Math.PI * 2;
    
    // Aurora Cyan (Top Left)
    const cyanX = width * 0.15 + Math.sin(angle) * 120;
    const cyanY = height * 0.2 + Math.cos(angle * 0.8) * 150;
    const cyanGlow = ctx.createRadialGradient(cyanX, cyanY, 50, cyanX, cyanY, 800);
    cyanGlow.addColorStop(0, "rgba(0, 242, 254, 0.08)");
    cyanGlow.addColorStop(1, "rgba(0, 242, 254, 0)");
    ctx.fillStyle = cyanGlow;
    ctx.fillRect(0, 0, width, height);

    // Aurora Pink (Bottom Right)
    const pinkX = width * 0.85 + Math.cos(angle * 1.2) * 150;
    const pinkY = height * 0.75 + Math.sin(angle * 0.9) * 200;
    const pinkGlow = ctx.createRadialGradient(pinkX, pinkY, 80, pinkX, pinkY, 900);
    pinkGlow.addColorStop(0, "rgba(255, 0, 127, 0.08)");
    pinkGlow.addColorStop(1, "rgba(255, 0, 127, 0)");
    ctx.fillStyle = pinkGlow;
    ctx.fillRect(0, 0, width, height);

    // Aurora Lime (Middle Right)
    const limeX = width * 0.7 + Math.sin(angle * 1.5) * 100;
    const limeY = height * 0.35 + Math.cos(angle * 1.1) * 120;
    const limeGlow = ctx.createRadialGradient(limeX, limeY, 30, limeX, limeY, 700);
    limeGlow.addColorStop(0, "rgba(190, 242, 100, 0.05)");
    limeGlow.addColorStop(1, "rgba(190, 242, 100, 0)");
    ctx.fillStyle = limeGlow;
    ctx.fillRect(0, 0, width, height);

    // 3. Squares Grid (Reactbits Squares style)
    const cols = Math.ceil(width / gridSize);
    const rows = Math.ceil(height / gridSize);

    for (let c = 0; c < cols; c++) {
      for (let r = 0; r < rows; r++) {
        const x = c * gridSize;
        const y = r * gridSize;

        // Base grid lines (very faint)
        ctx.strokeStyle = "rgba(255, 255, 255, 0.015)";
        ctx.lineWidth = 0.5;
        ctx.strokeRect(x, y, gridSize, gridSize);

        // Glow squares
        const glow = getCellGlow(c, r, frame);
        if (glow.active) {
          let color = "rgba(0, 242, 254, ";
          if (glow.colorType === 1) color = "rgba(255, 0, 127, ";
          if (glow.colorType === 2) color = "rgba(190, 242, 100, ";

          // Render glowing border
          ctx.strokeStyle = color + (glow.intensity * 0.14) + ")";
          ctx.lineWidth = 1.5;
          ctx.strokeRect(x + 1, y + 1, gridSize - 2, gridSize - 2);

          // Render glowing subtle fill
          ctx.fillStyle = color + (glow.intensity * 0.035) + ")";
          ctx.fillRect(x + 2, y + 2, gridSize - 4, gridSize - 4);
        }
      }
    }

    // 4. Generate floating particles (Reactbits Particles style)
    const particles = [];
    for (let i = 0; i < numParticles; i++) {
      particles.push(getParticle(i, frame));
    }

    // Draw connecting mesh lines between nearby particles
    ctx.lineWidth = 1;
    for (let i = 0; i < numParticles; i++) {
      for (let j = i + 1; j < numParticles; j++) {
        const p1 = particles[i];
        const p2 = particles[j];
        
        const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        const maxDist = 260;

        if (dist < maxDist) {
          const alpha = 0.12 * (1.0 - dist / maxDist);
          const lineGrad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
          
          let color1 = `rgba(0, 242, 254, ${alpha})`;
          if (p1.colorType === 1) color1 = `rgba(255, 0, 127, ${alpha})`;
          if (p1.colorType === 2) color1 = `rgba(190, 242, 100, ${alpha})`;

          let color2 = `rgba(0, 242, 254, ${alpha})`;
          if (p2.colorType === 1) color2 = `rgba(255, 0, 127, ${alpha})`;
          if (p2.colorType === 2) color2 = `rgba(190, 242, 100, ${alpha})`;

          lineGrad.addColorStop(0, color1);
          lineGrad.addColorStop(1, color2);

          ctx.strokeStyle = lineGrad;
          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.stroke();
        }
      }
    }

    // Draw particle glowing nodes
    for (let i = 0; i < numParticles; i++) {
      const p = particles[i];
      let colorStr = "#00f2fe"; // Cyan
      let shadowColor = "rgba(0, 242, 254, 0.4)";
      if (p.colorType === 1) {
        colorStr = "#ff007f"; // Pink
        shadowColor = "rgba(255, 0, 127, 0.4)";
      } else if (p.colorType === 2) {
        colorStr = "#bef264"; // Lime
        shadowColor = "rgba(190, 242, 100, 0.4)";
      }

      // Outer glow circle
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * 2.2, 0, Math.PI * 2);
      ctx.fillStyle = shadowColor.replace("0.4", "0.1");
      ctx.fill();

      // Inner core node
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fillStyle = colorStr;
      ctx.fill();
    }

    // 5. Tech scanlines overlay
    ctx.fillStyle = "rgba(0, 0, 0, 0.15)";
    for (let y = 0; y < height; y += 4) {
      ctx.fillRect(0, y, width, 1.5);
    }

  }, [frame, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
